<template>
  <div class="bbm-hubs-page">

    <!-- Hero -->
    <section class="bbm-hubs-hero">
      <div class="bbm-hubs-hero__content">
        <h1 class="bbm-hubs-hero__title">Premium Hubs in Brussels</h1>
        <p class="bbm-hubs-hero__sub">Discover the best workspaces, innovation centres and business hubs across the Brussels ecosystem.</p>
      </div>
    </section>

    <main class="bbm-hubs-main">

      <!-- Search + count -->
      <div class="tw-flex justify-between items-center" style="margin-bottom: 32px;">
        <p v-if="!loading" class="bbm-hubs-count">
          {{ filteredHubs.length }} {{ filteredHubs.length === 1 ? 'hub' : 'hubs' }}
        </p>
        <div class="bbm-search">
          <span class="material-symbols-outlined bbm-search__icon">search</span>
          <input v-model="searchQuery" class="bbm-search__input" placeholder="Search hubs..." type="text"/>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="n in 6" :key="n" class="bbm-skeleton-card">
          <div class="bbm-skeleton bbm-skeleton--image"></div>
          <div style="padding: 24px;">
            <div class="bbm-skeleton bbm-skeleton--title"></div>
            <div class="bbm-skeleton bbm-skeleton--text"></div>
            <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
            <div class="bbm-skeleton bbm-skeleton--btn" style="margin-top: 24px;"></div>
          </div>
        </div>
      </div>

      <!-- Hubs grid -->
      <div v-else-if="filteredHubs.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="hub in filteredHubs"
          :key="hub.id"
          class="bbm-hub-card"
          @click="$router.push(`/hubs-brussels/${hub.id}`)"
        >
          <div class="bbm-hub-card__image">
            <img :src="actorImage(hub)" :alt="hub.name"/>
            <span class="bbm-hub-card__badge">
              <span class="material-symbols-outlined" style="font-size:12px;">apartment</span>
              HUB
            </span>
          </div>
          <div class="bbm-hub-card__body">
            <h3 class="bbm-hub-card__title">{{ hub.name }}</h3>
            <p class="bbm-hub-card__desc">{{ actorDescription(hub) }}</p>
            <button class="bbm-hub-card__btn">View Hub</button>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="bbm-empty">
        <span class="material-symbols-outlined bbm-empty__icon">{{ searchQuery ? 'search_off' : 'apartment' }}</span>
        <p>{{ searchQuery ? `No hubs found for "${searchQuery}"` : 'No hubs available yet.' }}</p>
      </div>

    </main>

    <SimplifiedFooterBrussels/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { fetchActors } from '@/api/actors'
  import { getDefaultFallbackImageUrl } from '@/util/helpers'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'

  // All hub portfolio IDs from all pillars
  const HUB_PORTFOLIOS = [
    'B8R7Lz6gVY', // business-support
    'ZgkvQE6nro', // consulting
    '9jVOMldkOv', // entrepreneurship
    'AekDJgPV24', // talent
    'EJnJPRQxKq', // visibility
    'Mlkyedqky8', // workspace
  ]

  export default defineComponent({
    name: 'StaticHomeBrusselsHubsOverview',
    components: { SimplifiedFooterBrussels },
    data() {
      return {
        hubs: [] as any[],
        loading: false,
        searchQuery: '' as string,
      }
    },
    computed: {
      filteredHubs() {
        if (!this.searchQuery.trim()) return this.hubs
        const q = this.searchQuery.toLowerCase()
        return this.hubs.filter((h: any) =>
          (h.name || '').toLowerCase().includes(q) ||
          (h.short_description || h.activities_description || '').toLowerCase().includes(q)
        )
      },
    },
    async mounted() {
      this.loading = true
      const safe = (p: Promise<any>) => p.catch(() => ({ data: [] }))
      const results = await Promise.all(
        HUB_PORTFOLIOS.map(id => safe(fetchActors({ portfolio: id, limit: 20 })))
      )
      const seen = new Set<string>()
      const merged: any[] = []
      for (const result of results) {
        for (const hub of (result as any).data || []) {
          if (!seen.has(hub.id)) {
            seen.add(hub.id)
            merged.push(hub)
          }
        }
      }
      this.hubs = merged
      this.loading = false
    },
    methods: {
      actorImage(actor: any) {
        return actor.featured_image_url || actor.website_screenshot || getDefaultFallbackImageUrl()
      },
      actorDescription(actor: any): string {
        return actor.short_description || actor.activities_description || ''
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-accent: #ff6f31;
  $bbm-accent-dark: #802a00;
  $bbm-secondary-container: #00ccf9;
  $bbm-surface: #f7f9fb;

  .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

  .bbm-hubs-page { font-family: 'Inter', sans-serif; background: $bbm-surface; color: #191c1e; min-height: 100vh; }

  .bbm-hubs-hero {
    background: $bbm-primary; padding: 64px 32px; position: relative; overflow: hidden;
    &::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(0,30,64,0.5) 0%, transparent 60%); pointer-events: none; }
  }
  .bbm-hubs-hero__content { max-width: 1280px; margin: 0 auto; position: relative; z-index: 1; }
  .bbm-hubs-hero__title { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.75rem, 3vw, 3rem); font-weight: 700; color: white; margin: 0 0 12px; letter-spacing: -0.02em; }
  .bbm-hubs-hero__sub { font-size: 1.1rem; color: rgba(255,255,255,0.75); margin: 0; max-width: 560px; line-height: 1.6; }

  .bbm-hubs-main { max-width: 1280px; margin: 0 auto; padding: 48px 32px 80px; }

  .bbm-hubs-count { font-size: 0.9rem; color: #64748b; margin: 0; }

  .bbm-search {
    display: flex; align-items: center; gap: 8px; border: 1px solid #e2e8f0;
    border-radius: 8px; padding: 6px 12px; background: white;
  }
  .bbm-search__icon { color: #94a3b8; font-size: 1rem; }
  .bbm-search__input {
    border: none; outline: none; font-size: 0.875rem; width: 220px; background: transparent; color: #191c1e;
    &::placeholder { color: #94a3b8; }
  }

  .bbm-hub-card {
    background: white; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;
    cursor: pointer; transition: box-shadow 0.25s, transform 0.15s;
    &:hover { box-shadow: 0 10px 25px rgba(0,0,0,0.1); transform: translateY(-2px); }
  }
  .bbm-hub-card__image {
    height: 200px; position: relative; overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; .bbm-hub-card:hover & { transform: scale(1.04); } }
  }
  .bbm-hub-card__badge {
    position: absolute; top: 16px; left: 16px; background: $bbm-primary; color: white;
    font-size: 0.625rem; font-weight: 700; letter-spacing: 0.04em; padding: 4px 8px;
    border-radius: 2px; display: inline-flex; align-items: center; gap: 4px;
  }
  .bbm-hub-card__body { padding: 24px; }
  .bbm-hub-card__title { font-family: 'Space Grotesk', sans-serif; font-size: 1.1rem; font-weight: 600; color: $bbm-primary; margin: 0 0 8px; }
  .bbm-hub-card__desc {
    font-size: 0.875rem; line-height: 1.5; color: #475569; margin: 0 0 20px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .bbm-hub-card__btn {
    width: 100%; padding: 10px 0; background: $bbm-accent; color: white; font-weight: 700;
    font-size: 0.875rem; border: none; border-radius: 8px; cursor: pointer; transition: background 0.2s;
    &:hover { background: $bbm-accent-dark; }
  }

  .bbm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; color: #94a3b8; gap: 12px; }
  .bbm-empty__icon { font-size: 3rem; opacity: 0.5; }

  @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
  .bbm-skeleton-card { background: white; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
  .bbm-skeleton {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400px 100%; animation: shimmer 1.4s infinite; border-radius: 4px;
  }
  .bbm-skeleton--image { height: 200px; border-radius: 0; }
  .bbm-skeleton--title { height: 20px; width: 70%; margin-bottom: 12px; }
  .bbm-skeleton--text { height: 14px; width: 90%; margin-bottom: 8px; }
  .bbm-skeleton--btn { height: 36px; width: 100%; border-radius: 8px; }

  @media (max-width: 768px) {
    .bbm-hubs-hero { padding: 40px 16px; }
    .bbm-hubs-main { padding: 32px 16px 64px; }
  }
</style>
